import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Chip,
  Stack,
  Menu,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { blue } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import UICard, { UICardContent, UICardHeading } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';
import { DataGridSimpleStyled } from '@/components/Shared/DataGridSimple';
import { useResellerContext } from './utils/contexts';

export default function ResellerDashboard() {
  const navigate = useNavigate();
  const { 
    sidebar, 
    customers, 
    stats, 
    loading, 
    error, 
    refreshCustomers, 
    refreshStats 
  } = useResellerContext();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.plan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCustomerId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action: string) => {
    handleMenuClose();
    
    if (!selectedCustomerId) return;
    
    switch (action) {
      case 'view':
        navigate(`/resellers/customers/detail/${selectedCustomerId}`);
        break;
      case 'edit':
        navigate(`/resellers/customers/edit/${selectedCustomerId}`);
        break;
      case 'upgrade':
        console.log('Upgrading customer plan:', selectedCustomerId);
        break;
      case 'suspend':
        console.log('Suspending customer:', selectedCustomerId);
        break;
      default:
        break;
    }
  };

  const handleEnrollClick = () => {
    navigate('/resellers/enroll');
  };

  const columns = [
    { field: 'name', headerName: 'Customer Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'plan', headerName: 'Plan', width: 130 },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 130,
      renderCell: (params: any) => (
        <Chip 
          label={params.value} 
          size="small" 
          color={
            params.value === 'Active' ? 'success' : 
            params.value === 'Pending' ? 'warning' : 
            'error'
          }
          sx={{ fontSize: 12 }}
        />
      )
    },
    { field: 'revenue', headerName: 'Revenue', width: 130 },
    { 
      field: 'products', 
      headerName: 'Products', 
      width: 250,
      renderCell: (params: any) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {params.value.map((product: string) => (
            <Chip 
              key={product} 
              label={product} 
              size="small" 
              sx={{ fontSize: 11, mb: 0.5 }}
            />
          ))}
        </Stack>
      )
    },
    { field: 'createTime', headerName: 'Created', width: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params: any) => (
        <Box sx={{ position: 'relative' }}>
          <Utils.FormUtils.PrimaryButton
            variant="text"
            color="primary"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleActionClick(e, params.row.id)}
            sx={{ 
              minWidth: '120px', 
              justifyContent: 'space-between',
              fontSize: 13,
              fontWeight: 500
            }}
          >
            Manage
          </Utils.FormUtils.PrimaryButton>
        </Box>
      )
    }
  ];

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Box my="auto" width="75%">
                <Utils.LayoutUtils.MainHeading
                  title="Reseller Dashboard"
                  description="Manage your customers, view revenue, and track performance metrics"
                />
              </Box>
              <Stack my="auto" width="25%" direction="row" justifyContent="end">
                <Utils.CreateObjectButton
                  href="/resellers/customers/create"
                  text="Add Customer"
                />
              </Stack>
            </Stack>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <UICard gradient sx={{ background: `linear-gradient(to right, ${blue[50]}, ${blue[100]})`, mb: 2 }}>
                  <UICardHeading
                    title="Become a Reseller Partner"
                    description="Join our partner program and grow your business with our cloud services"
                  />
                  <UICardContent>
                    <Box p={2}>
                      <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={8}>
                          <Typography variant="h6" fontWeight={500} fontSize={16} letterSpacing={0.75} gutterBottom>
                            Partner Benefits
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: 'primary.main', 
                                  mr: 1 
                                }} />
                                <Typography fontSize={14}>Up to 25% commission on sales</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: 'primary.main', 
                                  mr: 1 
                                }} />
                                <Typography fontSize={14}>Dedicated partner support</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: 'primary.main', 
                                  mr: 1 
                                }} />
                                <Typography fontSize={14}>Marketing resources and materials</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ 
                                  width: 6, 
                                  height: 6, 
                                  borderRadius: '50%', 
                                  bgcolor: 'primary.main', 
                                  mr: 1 
                                }} />
                                <Typography fontSize={14}>Customer management tools</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Utils.FormUtils.PrimaryButton
                            variant="contained"
                            startIcon={<BusinessCenterIcon />}
                            onClick={handleEnrollClick}
                            fullWidth
                            sx={{ py: 1.5 }}
                          >
                            Enroll as a Reseller Partner
                          </Utils.FormUtils.PrimaryButton>
                        </Grid>
                      </Grid>
                    </Box>
                  </UICardContent>
                </UICard>
              </Grid>
              
              {error.stats && (
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => refreshStats()}>
                    {error.stats} - Click to retry
                  </Alert>
                </Grid>
              )}
              
              <Grid item xs={12} md={4}>
                <UICard>
                  <UICardHeading title="Customers" />
                  <UICardContent>
                    {loading.stats ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : stats ? (
                      <Box p={2}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography color="text.secondary" fontSize={14}>Total</Typography>
                            <Typography variant="h5" fontWeight={600}>{stats.totalCustomers}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography color="text.secondary" fontSize={14}>Active</Typography>
                            <Typography variant="h5" fontWeight={600}>{stats.activeCustomers}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Box p={2}>
                        <Typography>No data available</Typography>
                      </Box>
                    )}
                  </UICardContent>
                </UICard>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <UICard>
                  <UICardHeading title="Revenue" />
                  <UICardContent>
                    {loading.stats ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : stats ? (
                      <Box p={2}>
                        <Typography color="text.secondary" fontSize={14}>Total Revenue</Typography>
                        <Typography variant="h5" fontWeight={600}>${stats.totalRevenue.toLocaleString()}</Typography>
                        <Box mt={1}>
                          <Typography color="text.secondary" fontSize={14}>Your Commission</Typography>
                          <Typography fontSize={16} fontWeight={500} color={blue[700]}>
                            ${(stats.totalRevenue * stats.commissionRate).toLocaleString()} ({stats.commissionRate * 100}%)
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box p={2}>
                        <Typography>No data available</Typography>
                      </Box>
                    )}
                  </UICardContent>
                </UICard>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <UICard>
                  <UICardHeading title="Quick Actions" />
                  <UICardContent>
                    <Box p={2}>
                      <Stack spacing={1.5}>
                        <Utils.FormUtils.PrimaryButton
                          variant="outlined"
                          startIcon={<PersonAddIcon />}
                          onClick={() => navigate('/resellers/customers/create')}
                          fullWidth
                          size="small"
                        >
                          Add New Customer
                        </Utils.FormUtils.PrimaryButton>
                        <Utils.FormUtils.PrimaryButton
                          variant="outlined"
                          onClick={() => navigate('/resellers/products')}
                          fullWidth
                          size="small"
                        >
                          View Product Catalog
                        </Utils.FormUtils.PrimaryButton>
                        <Utils.FormUtils.PrimaryButton
                          variant="outlined"
                          onClick={() => navigate('/resellers/billing')}
                          fullWidth
                          size="small"
                        >
                          Manage Billing
                        </Utils.FormUtils.PrimaryButton>
                      </Stack>
                    </Box>
                  </UICardContent>
                </UICard>
              </Grid>
              
              <Grid item xs={12}>
                <UICard>
                  <UICardHeading 
                    title="Customers" 
                    description="View and manage your customer accounts"
                  >
                    <Utils.FormUtils.PrimaryInput
                      placeholder="Search customers..."
                      value={searchTerm}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                      size="small"
                      sx={{ minWidth: '250px' }}
                    />
                  </UICardHeading>
                  <UICardContent>
                    {error.customers && (
                      <Alert severity="error" onClose={() => refreshCustomers()} sx={{ mb: 2 }}>
                        {error.customers} - Click to retry
                      </Alert>
                    )}
                    
                    {loading.customers ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <DataGridSimpleStyled
                        rows={filteredCustomers}
                        columns={columns}
                        autoHeight
                        disableSelectionOnClick
                        pageSize={5}
                        rowsPerPageOptions={[5, 10, 25]}
                        onRowClick={(params: any) => navigate(`/resellers/customers/detail/${params.id}`)}
                        disableColumnMenu
                      />
                    )}
                  </UICardContent>
                </UICard>
              </Grid>
            </Grid>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              elevation={2}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleAction('view')}>View Details</MenuItem>
              <MenuItem onClick={() => handleAction('edit')}>Edit Customer</MenuItem>
              <MenuItem onClick={() => handleAction('upgrade')}>Upgrade Plan</MenuItem>
              <MenuItem onClick={() => handleAction('suspend')} sx={{ color: 'error.main' }}>Suspend Account</MenuItem>
            </Menu>
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>
    </Grid>
  );
} 